import type { PageData } from "./types";
import type { TbHttp } from "./http";
import type { EntityRef } from "./types";

export type TbAlarmSeverity = "CRITICAL" | "MAJOR" | "MINOR" | "WARNING" | "INDETERMINATE";
export type TbAlarmStatus   = "ACTIVE_UNACK" | "ACTIVE_ACK" | "CLEARED_UNACK" | "CLEARED_ACK";

export type TbAlarm = {
  id: { id: string; entityType: "ALARM" };
  createdTime: number;
  name: string;
  type: string;
  originator: { id: string; entityType: string };
  originatorName?: string;
  severity: TbAlarmSeverity;
  status: TbAlarmStatus;
  startTs: number;
  endTs?: number;
  ackTs?: number;
  clearTs?: number;
  details?: Record<string, unknown>;
};

export function getEntityAlarms(
  http: TbHttp,
  entity: EntityRef,
  opts: { pageSize?: number; page?: number; searchStatus?: "ACTIVE" | "CLEARED" | "ACK" | "UNACK" | "ANY"; startTs?: number; endTs?: number } = {},
): Promise<PageData<TbAlarm>> {
  const p = new URLSearchParams({
    pageSize: String(opts.pageSize ?? 50),
    page: String(opts.page ?? 0),
    sortProperty: "createdTime",
    sortOrder: "DESC",
    searchStatus: opts.searchStatus ?? "ACTIVE",
    fetchOriginator: "true",
  });
  if (opts.startTs) p.set("startTime", String(opts.startTs));
  if (opts.endTs) p.set("endTime", String(opts.endTs));
  return http.get(`/api/alarm/${entity.type}/${entity.id}?${p}`);
}

export function ackAlarm(http: TbHttp, alarmId: string): Promise<void> {
  return http.post(`/api/alarm/${alarmId}/ack`, undefined);
}
export function clearAlarm(http: TbHttp, alarmId: string): Promise<void> {
  return http.post(`/api/alarm/${alarmId}/clear`, undefined);
}

/** TB 3.5+ alarm comments. */
export function addAlarmComment(http: TbHttp, alarmId: string, text: string): Promise<unknown> {
  return http.post(`/api/alarm/${alarmId}/comment`, { comment: { text } });
}

/**
 * One row of TB's `AlarmCommentInfo` DTO (TB 3.5+). `type: "SYSTEM"` rows
 * are auto-generated audit lines (ack/clear/assign); operator notes are
 * `type: "OTHER"`. The author's name fields come from the joined user.
 */
export type TbAlarmCommentInfo = {
  id: { id: string; entityType: "ALARM_COMMENT" };
  createdTime: number;
  userId?: { id: string; entityType: "USER" } | null;
  type?: "OTHER" | "SYSTEM";
  comment?: { text?: string; [k: string]: unknown };
  firstName?: string;
  lastName?: string;
  email?: string;
};

/** GET sibling of {@link addAlarmComment} — the alarm's comment thread,
 *  oldest first. Fetch lazily (row expand), never per-alarm in list calls. */
export function getAlarmComments(
  http: TbHttp,
  alarmId: string,
  opts: { pageSize?: number; page?: number } = {},
): Promise<PageData<TbAlarmCommentInfo>> {
  const p = new URLSearchParams({
    pageSize: String(opts.pageSize ?? 100),
    page: String(opts.page ?? 0),
    sortProperty: "createdTime",
    sortOrder: "ASC",
  });
  return http.get(`/api/alarm/${alarmId}/comment?${p}`);
}
